/**
 * Tâche 1339 (Eric 2026-06-30) — Journal des retouches à la main.
 *
 * La tâche 1332 a ouvert la réécriture d'une phrase directement dans la
 * transcription fusionnée. Utile, mais dangereux sans trace : une fois la
 * phrase remplacée, plus rien à l'écran ne disait QUI l'avait réécrite, NI ce
 * qu'elle disait avant. Cette fenêtre liste toutes les phrases retouchées,
 * avec leur auteur, leur date, la version d'avant, et un bouton pour y
 * revenir en un clic.
 *
 * Les données viennent des segments déjà chargés (`manual_edit`) : aucune
 * requête supplémentaire à l'ouverture.
 */
import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, ClockCounterClockwise, ArrowUUpLeft, CircleNotch } from "@phosphor-icons/react";

export default function ManualEditsJournalModal({
  open,
  onClose,
  api,
  videoId,
  segments = [],
  canEdit = false,
  onRestored,
  lang = "fr",
}) {
  const [restoringId, setRestoringId] = useState(null);
  if (!open) return null;

  const retouches = segments
    .map((s, i) => (s.manual_edit ? { seg: s, index: i } : null))
    .filter(Boolean)
    .sort((a, b) => String(b.seg.manual_edit.at || "").localeCompare(String(a.seg.manual_edit.at || "")));

  const revenirEnArriere = async (seg) => {
    const avant = seg.manual_edit?.previous_text;
    if (!avant || restoringId) return;
    setRestoringId(seg.id);
    try {
      await axios.patch(
        `${api}/api/videos/${videoId}/merged-segments/${seg.id}/text`,
        { text: avant },
        { withCredentials: true },
      );
      toast.success(lang === "fr" ? "Version d'avant rétablie." : "Previous version restored.");
      if (onRestored) await onRestored(seg.id);
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message
        || (lang === "fr" ? "Erreur inconnue" : "Unknown error");
      toast.error((lang === "fr" ? "Retour impossible : " : "Restore failed: ") + detail);
    } finally {
      setRestoringId(null);
    }
  };

  const allerALaPhrase = (index) => {
    window.dispatchEvent(new CustomEvent("knoria:merged-focus-segment", {
      detail: { videoId, segmentIndex: index },
    }));
    onClose();
  };

  return (
    <div
      className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="manual-edits-journal-modal"
    >
      <div
        className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[85vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-4 border-b border-zinc-200 flex items-center justify-between bg-zinc-50">
          <div>
            <h3 className="font-bold text-base text-zinc-900 flex items-center gap-2">
              <ClockCounterClockwise size={18} weight="bold" className="text-amber-600" />
              {lang === "fr" ? "Retouches à la main" : "Hand edits"}
            </h3>
            <p className="text-[11px] text-zinc-500 mt-0.5">
              {lang === "fr"
                ? "Chaque phrase réécrite à la main, avec son auteur et sa version d'avant."
                : "Every hand-rewritten sentence, with its author and previous version."}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-zinc-200 rounded"
            data-testid="close-manual-edits-journal-btn"
          >
            <X size={18} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto">
          {retouches.length === 0 ? (
            <div className="p-8 text-center text-zinc-500 text-sm" data-testid="manual-edits-journal-empty">
              {lang === "fr"
                ? "Aucune phrase n'a été réécrite à la main. Le crayon devant chaque phrase sert à cela."
                : "No sentence has been hand-rewritten yet. That is what the pencil in front of each sentence is for."}
            </div>
          ) : (
            <ul className="divide-y divide-zinc-100">
              {retouches.map(({ seg, index }) => {
                const quand = seg.manual_edit.at ? new Date(seg.manual_edit.at) : null;
                return (
                  <li
                    key={seg.id}
                    className="px-5 py-3.5"
                    data-testid={`manual-edit-entry-${index}`}
                  >
                    <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                      <span className="inline-block px-1.5 py-0.5 text-[10px] font-semibold rounded bg-amber-100 text-amber-900 uppercase tracking-wide">
                        {lang === "fr" ? "Réécriture" : "Rewrite"}
                      </span>
                      <span className="text-[11px] text-zinc-500">
                        {lang === "fr" ? "Par" : "By"}{" "}
                        <span className="font-medium text-zinc-700">{seg.manual_edit.by || "—"}</span>
                      </span>
                      <span className="text-[11px] text-zinc-400">
                        {quand ? quand.toLocaleString(lang === "fr" ? "fr-CA" : "en-CA") : "—"}
                      </span>
                      <span className="text-[11px] text-zinc-400">
                        {lang === "fr" ? `Phrase ${index + 1}` : `Sentence ${index + 1}`}
                      </span>
                      <button
                        type="button"
                        onClick={() => allerALaPhrase(index)}
                        className="text-[11px] font-semibold text-[#002FA7] hover:underline"
                        data-testid={`manual-edit-goto-${index}`}
                      >
                        {lang === "fr" ? "Voir dans la transcription" : "Show in transcript"}
                      </button>
                    </div>
                    <div className="grid md:grid-cols-2 gap-2">
                      <div className="rounded border border-zinc-200 bg-zinc-50 px-2.5 py-2">
                        <p className="text-[10px] uppercase tracking-wide text-zinc-400 font-semibold mb-1">
                          {lang === "fr" ? "Version d'avant" : "Previous version"}
                        </p>
                        <p
                          className="text-[12px] text-zinc-600 leading-snug whitespace-pre-wrap line-through"
                          data-testid={`manual-edit-previous-${index}`}
                        >
                          {seg.manual_edit.previous_text || "—"}
                        </p>
                      </div>
                      <div className="rounded border border-emerald-200 bg-emerald-50 px-2.5 py-2">
                        <p className="text-[10px] uppercase tracking-wide text-emerald-700 font-semibold mb-1">
                          {lang === "fr" ? "Texte actuel" : "Current text"}
                        </p>
                        <p
                          className="text-[12px] text-zinc-800 leading-snug whitespace-pre-wrap"
                          data-testid={`manual-edit-current-${index}`}
                        >
                          {seg.merged_text || seg.text || "—"}
                        </p>
                      </div>
                    </div>
                    {canEdit && seg.manual_edit.previous_text && (
                      <button
                        type="button"
                        onClick={() => revenirEnArriere(seg)}
                        disabled={restoringId === seg.id}
                        className="mt-2 text-[11px] font-semibold px-3 py-1.5 rounded border border-zinc-300 text-zinc-700 hover:bg-zinc-50 disabled:opacity-60 inline-flex items-center gap-1.5"
                        data-testid={`manual-edit-restore-${index}`}
                      >
                        {restoringId === seg.id
                          ? <CircleNotch size={12} className="animate-spin" />
                          : <ArrowUUpLeft size={12} weight="bold" />}
                        {lang === "fr" ? "Revenir à la version d'avant" : "Restore previous version"}
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
